/**
 * ① 変更一覧 (データ変更履歴。マスタ変更履歴 SBI 7)。
 *
 * 呼び出し元が権限を持つ全種別の変更を新しい順に表示する。マスタ種別と操作で絞り込める。
 * 変更発生日 (から〜まで) と最終更新者でも絞り込める。データ種別が放送地点・任意テキストのときだけ、
 * 線区 (「全線区」を含む) でも絞り込める。いずれも対象ごとの最新の変更に対して効く (API 側で絞り込む)。
 * 行を押すと、その対象の ② (対象データの履歴一覧) へ遷移する。
 *
 * 各行の「データ復旧」ボタンは、最終操作が「削除」の行 (端末を除く) だけ押せる。確認ダイアログのあとで
 * その削除を記録した版 (= 一覧の行の版) を `restore` に渡し、削除前の内容で作り直す。同じキーの
 * データが既にあるときは API が 409 で拒否し、その理由 (日本語) をそのまま表示する。
 * 成功すると一覧を取り直す (復旧した対象の最終操作は「新規登録」になり、ボタンは押せなくなる)。
 *
 * マスタ種別の選択肢は、利用者が機能を持つ種別だけを出す (持たない種別を選ぶと API は 403)。
 * URL の `master_type` が権限のない種別を指していた場合は、選択肢に無いので「すべて」に戻し、
 * その旨を案内する (403 を画面に出さない)。
 *
 * 絞り込み条件は URL に載せず、画面の状態として持つ (操作の絞り込みと同じ)。
 * 最終更新者は入力のたびに API を呼ばないよう、入力が止まってから反映する。
 */
import { useEffect, useMemo, useState } from 'react';
import type React from 'react';

import { MasterHistoryClient } from '../api/masterHistory';
import type { MasterHistoryAction, MasterHistoryListEntry, MasterHistoryType } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import {
  FREE_TEXT_TEMPLATE_ALL_ROUTES,
  FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL,
} from '../data/freeTextTemplates';
import { fetchRoutesMaster, type RouteMaster } from '../data/routes';
import { useMasterHistoryPages } from '../hooks/useMasterHistoryPages';
import { buildEntityPath, buildListPath } from '../pages/masterHistoryView';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import {
  MASTER_HISTORY_ACTION_LABEL,
  MASTER_HISTORY_TYPE_LABEL,
} from '../utils/masterHistoryLabels';

import { ConfirmDialog } from './ConfirmDialog';
import { MasterHistorySourceBackButton } from './MasterHistorySourceBackButton';
import { MasterHistoryTable } from './MasterHistoryTable';
import { StatusBanner } from './StatusBanner';

export interface MasterHistoryListScreenProps {
  config: AppConfig;
  /** URL の `master_type` (未指定・未知は undefined) */
  masterType: MasterHistoryType | undefined;
  /** 利用者が履歴を見られるマスタ種別 (選択肢に出す) */
  allowedTypes: readonly MasterHistoryType[];
  onNavigate: (path: string) => void;
  /** ヘッダーのタブから入ったとき true (「閉じる」「遷移元へ戻る」を出さない) */
  hideSourceBack?: boolean;
}

export const UNAVAILABLE_TYPE_MESSAGE =
  '指定されたデータ種別の履歴を表示する権限がないため、すべての種別を表示しています';

export const RESTORE_CONFIRM_TITLE = 'データ復旧';
export const RESTORE_CONFIRM_MESSAGE =
  '削除されたデータを、削除する前の内容で復旧します。復旧は新しい変更として履歴に残ります。\n実行してよろしいですか？';
export const RESTORE_CONFIRM_LABEL = '復旧する';
export const RESTORE_SUCCESS_MESSAGE = 'データを復旧しました';
export const RESTORE_HISTORY_FAILED_MESSAGE =
  '復旧しましたが、変更履歴の記録に失敗しました。履歴に残っていない可能性があります';

export const INVALID_DATE_RANGE_MESSAGE = '変更発生日は「から」を「まで」以前の日付にしてください';
export const ROUTES_UNAVAILABLE_MESSAGE =
  '線区の一覧を取得できませんでした。線区での絞り込みは使えません';

const ACTIONS: readonly MasterHistoryAction[] = ['CREATE', 'UPDATE', 'DELETE', 'REBOOT'];

/**
 * 線区で絞り込めるデータ種別。履歴の `entity_key` が `<種別>#<線区>#...` の形のもの。
 * ⚠️ `lambda/control-panel-api/master_change_history.py` の `ROUTE_FILTER_MASTER_TYPES` と一致させる
 * (サーバは他の種別への `route_id` を 400 にする)。
 */
const ROUTE_FILTER_TYPES: readonly MasterHistoryType[] = ['broadcast_spot', 'free_text_template'];

/** 「全線区共通」を選べるのは任意テキストだけ (放送地点に全線区共通は無い) */
const ALL_ROUTES_COMMON_TYPE: MasterHistoryType = 'free_text_template';

/** 最終更新者の入力が止まってから条件に反映するまでの待ち時間 (ミリ秒) */
const ACTOR_DEBOUNCE_MS = 400;

/** 最終更新者の最大文字数。⚠️ サーバの `MAX_ACTOR_FILTER_LENGTH` と一致させる */
const ACTOR_MAX_LENGTH = 254;

/** 線区の選択。どのデータ種別で選んだかを持ち、種別が変わったら無効にする */
interface RouteChoice {
  type: MasterHistoryType | undefined;
  routeId: string;
}

export function MasterHistoryListScreen(props: MasterHistoryListScreenProps): React.JSX.Element {
  const { config, masterType, allowedTypes, onNavigate, hideSourceBack = false } = props;
  const { getIdToken } = useAuth();
  const client = useMemo(
    () => new MasterHistoryClient({ getIdToken, config }),
    [getIdToken, config],
  );

  const [action, setAction] = useState<MasterHistoryAction | ''>('');
  const [changedFrom, setChangedFrom] = useState('');
  const [changedTo, setChangedTo] = useState('');
  const [actorDraft, setActorDraft] = useState('');
  const [actor, setActor] = useState('');
  const [routeChoice, setRouteChoice] = useState<RouteChoice>({ type: undefined, routeId: '' });
  const typeUnavailable = masterType !== undefined && !allowedTypes.includes(masterType);
  const effectiveType = typeUnavailable ? undefined : masterType;

  const routeFilterEnabled =
    effectiveType !== undefined && ROUTE_FILTER_TYPES.includes(effectiveType);
  // 種別を変えたら「全線区」に戻す (別の種別の線区を持ち越さない)。
  const routeId =
    routeFilterEnabled && routeChoice.type === effectiveType ? routeChoice.routeId : '';
  const dateRangeInvalid = changedFrom !== '' && changedTo !== '' && changedFrom > changedTo;

  useEffect(() => {
    const trimmed = actorDraft.trim();
    const timer = setTimeout(() => setActor(trimmed), ACTOR_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [actorDraft]);

  const [routes, setRoutes] = useState<RouteMaster[] | null>(null);
  const [routesFailed, setRoutesFailed] = useState(false);
  useEffect(() => {
    if (!routeFilterEnabled || routes !== null) return;
    let aborted = false;
    fetchRoutesMaster()
      .then((master) => {
        if (!aborted) setRoutes(master.routes);
      })
      .catch(() => {
        // 線区が選べないだけで、他の絞り込みと一覧は使える。
        if (aborted) return;
        setRoutes([]);
        setRoutesFailed(true);
      });
    return () => {
      aborted = true;
    };
  }, [routeFilterEnabled, routes]);

  const fetchPage = useMemo(
    () => (nextToken: string | undefined) => {
      // 期間が逆のときは API を呼ばない (サーバは 400 にするだけで、画面の案内の方が分かりやすい)。
      if (dateRangeInvalid) return Promise.resolve({ items: [], count: 0 });
      return client.list({
        masterType: effectiveType,
        action: action === '' ? undefined : action,
        changedFrom: changedFrom === '' ? undefined : changedFrom,
        changedTo: changedTo === '' ? undefined : changedTo,
        actor: actor === '' ? undefined : actor,
        routeId: routeId === '' ? undefined : routeId,
        nextToken,
      });
    },
    [client, effectiveType, action, changedFrom, changedTo, actor, routeId, dateRangeInvalid],
  );
  const pages = useMasterHistoryPages(
    fetchPage,
    [effectiveType ?? '', action, changedFrom, changedTo, actor, routeId].join('|'),
  );
  const errorMessage = pages.error === null ? null : describeApiErrorMessage(pages.error);

  const [restoreTarget, setRestoreTarget] = useState<MasterHistoryListEntry | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreResult, setRestoreResult] = useState<{
    name: string;
    historyOk: boolean;
  } | null>(null);

  function runRestore(entry: MasterHistoryListEntry): void {
    setRestoring(true);
    setRestoreError(null);
    setRestoreResult(null);
    client
      .restore(entry.version, entry.entity_key)
      .then((result) => {
        setRestoreResult({
          name: entry.display_name || '(名称なし)',
          historyOk: result.historyRecordSucceeded,
        });
        pages.reload();
      })
      .catch((err: unknown) => {
        // 409 (同じキーのデータが既にある / 参照先が無い等) はサーバの日本語の理由をそのまま出す。
        setRestoreError(describeApiErrorMessage(err));
      })
      .finally(() => {
        setRestoring(false);
      });
  }

  return (
    <section className="section section--page list-page" data-testid="master-history-list">
      <h2>データ変更履歴</h2>
      {typeUnavailable && (
        <p role="status" data-testid="master-history-type-notice">
          {UNAVAILABLE_TYPE_MESSAGE}
        </p>
      )}
      <div className="sort-filter-bar">
        <MasterHistorySourceBackButton
          masterType={effectiveType}
          hidden={hideSourceBack}
          onNavigate={onNavigate}
          testId="master-history-list-back-source"
        />
        <label className="sort-filter-select-label">
          データ種別
          <select
            value={effectiveType ?? ''}
            onChange={(e) => {
              const next = e.target.value as MasterHistoryType | '';
              onNavigate(buildListPath(next === '' ? undefined : next));
            }}
            data-testid="master-history-type-filter"
          >
            <option value="">すべて</option>
            {allowedTypes.map((type) => (
              <option key={type} value={type}>
                {MASTER_HISTORY_TYPE_LABEL[type]}
              </option>
            ))}
          </select>
        </label>
        <label className="sort-filter-select-label">
          最終操作
          <select
            value={action}
            onChange={(e) => setAction(e.target.value as MasterHistoryAction | '')}
            data-testid="master-history-action-filter"
          >
            <option value="">すべて</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {MASTER_HISTORY_ACTION_LABEL[a]}
              </option>
            ))}
          </select>
        </label>
        {routeFilterEnabled && (
          <label className="sort-filter-select-label">
            線区
            <select
              value={routeId}
              onChange={(e) => setRouteChoice({ type: effectiveType, routeId: e.target.value })}
              data-testid="master-history-route-filter"
            >
              <option value="">全線区</option>
              {effectiveType === ALL_ROUTES_COMMON_TYPE && (
                <option value={FREE_TEXT_TEMPLATE_ALL_ROUTES}>
                  {FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL}
                </option>
              )}
              {(routes ?? []).map((r) => (
                <option key={r.route_id} value={r.route_id}>
                  {r.route_name} ({r.route_id})
                </option>
              ))}
            </select>
          </label>
        )}
        <div
          className="sort-filter-select-label"
          role="group"
          aria-label="変更発生日"
          data-testid="master-history-date-filter"
        >
          <span>変更発生日</span>
          <input
            type="date"
            aria-label="変更発生日（から）"
            value={changedFrom}
            max={changedTo || undefined}
            onChange={(e) => setChangedFrom(e.target.value)}
            data-testid="master-history-changed-from"
          />
          <span aria-hidden="true">〜</span>
          <input
            type="date"
            aria-label="変更発生日（まで）"
            value={changedTo}
            min={changedFrom || undefined}
            onChange={(e) => setChangedTo(e.target.value)}
            data-testid="master-history-changed-to"
          />
        </div>
        <label className="sort-filter-select-label">
          最終更新者
          <input
            type="text"
            value={actorDraft}
            maxLength={ACTOR_MAX_LENGTH}
            placeholder="メールアドレスなど（部分一致）"
            onChange={(e) => setActorDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setActor(actorDraft.trim());
            }}
            data-testid="master-history-actor-filter"
          />
        </label>
      </div>
      {dateRangeInvalid && (
        <p role="alert" data-testid="master-history-date-range-error">
          {INVALID_DATE_RANGE_MESSAGE}
        </p>
      )}
      {routeFilterEnabled && routesFailed && (
        <p role="status" data-testid="master-history-routes-notice">
          {ROUTES_UNAVAILABLE_MESSAGE}
        </p>
      )}
      {restoreError !== null && (
        <StatusBanner
          variant="error"
          onDismiss={() => setRestoreError(null)}
          data-testid="master-history-list-restore-error"
        >
          {restoreError}
        </StatusBanner>
      )}
      {restoreResult !== null && (
        <>
          <p
            className="success-message"
            role="status"
            data-testid="master-history-list-restore-done"
          >
            {RESTORE_SUCCESS_MESSAGE}（{restoreResult.name}）
          </p>
          {!restoreResult.historyOk && (
            <StatusBanner
              variant="warning"
              data-testid="master-history-list-restore-history-warning"
            >
              {RESTORE_HISTORY_FAILED_MESSAGE}
            </StatusBanner>
          )}
        </>
      )}
      <MasterHistoryTable
        items={pages.items}
        loading={pages.loading}
        loadingMore={pages.loadingMore}
        errorMessage={errorMessage}
        hasMore={pages.hasMore}
        showTarget
        onSelect={(entry: MasterHistoryListEntry) => onNavigate(buildEntityPath(entry.entity_key))}
        onRestore={(entry: MasterHistoryListEntry) => setRestoreTarget(entry)}
        restoreBusy={restoring}
        onLoadMore={pages.loadMore}
        onReload={pages.reload}
        testIdPrefix="master-history-list"
      />
      <ConfirmDialog
        open={restoreTarget !== null}
        title={RESTORE_CONFIRM_TITLE}
        message={RESTORE_CONFIRM_MESSAGE}
        confirmLabel={RESTORE_CONFIRM_LABEL}
        destructive
        onConfirm={() => {
          // 先に閉じてから実行する (実行中に確認が残って二重に押されないようにする)。
          const target = restoreTarget;
          setRestoreTarget(null);
          if (target !== null) runRestore(target);
        }}
        onCancel={() => setRestoreTarget(null)}
      />
    </section>
  );
}
